/**
 * New Syndication Round creation page.
 * Accepts ?pipeline_id=, ?startup_id=, ?project_id= query params to pre-fill.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import syndicationService from '../services/syndicationService';
import dealPipelineService from '../services/dealPipelineService';
import projectService from '../services/projectService';
import {
  CoInvestVisibility,
  InstrumentType,
  SyndicationStatus,
} from '../types/syndication';
import { DealPipeline } from '../types/dealPipeline';
import { Project } from '../types/project';

const NewSyndicationRoundPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledPipelineId = searchParams.get('pipeline_id');
  const prefilledStartupId = searchParams.get('startup_id');
  const prefilledProjectId = searchParams.get('project_id');

  const [roundName, setRoundName] = useState('');
  const [pipelineId, setPipelineId] = useState<number | ''>(
    prefilledPipelineId ? Number(prefilledPipelineId) : ''
  );
  const [startupId, setStartupId] = useState<number | ''>(
    prefilledStartupId ? Number(prefilledStartupId) : ''
  );
  const [projectId, setProjectId] = useState<number | ''>(
    prefilledProjectId ? Number(prefilledProjectId) : ''
  );
  const [targetAmount, setTargetAmount] = useState<string>('');
  const [minimumCommitment, setMinimumCommitment] = useState<string>('');
  const [maximumCommitment, setMaximumCommitment] = useState<string>('');
  const [instrumentType, setInstrumentType] = useState<InstrumentType>('safe');
  const [carry, setCarry] = useState<string>('20');
  const [mgmtFee, setMgmtFee] = useState<string>('2');
  const [adminFee, setAdminFee] = useState<string>('');
  const [visibility, setVisibility] =
    useState<CoInvestVisibility>('transparent');
  const [status, setStatus] = useState<SyndicationStatus>('draft');
  const [openDate, setOpenDate] = useState<string>('');
  const [closeDate, setCloseDate] = useState<string>('');

  const [pipelines, setPipelines] = useState<DealPipeline[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [pipelineRes, projectRes] = await Promise.all([
        dealPipelineService.fetchPipelines({ size: 200 }),
        projectService.fetchProjects({ size: 200 }),
      ]);
      if (pipelineRes.data) setPipelines(pipelineRes.data.pipelines);
      if (projectRes.data) setProjects(projectRes.data.projects);
    };
    load();
  }, []);

  // Auto-fill from pipeline selection
  useEffect(() => {
    if (pipelineId === '') return;
    const p = pipelines.find((x) => x.id === pipelineId);
    if (p) {
      if (p.project_id && projectId === '') setProjectId(p.project_id);
      if (p.startup_id && startupId === '') setStartupId(p.startup_id);
      if (!roundName) {
        const target =
          p.project?.project_name || p.startup?.name || `Pipeline ${p.id}`;
        setRoundName(`${target} Syndication Round`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineId, pipelines]);

  const submit = async () => {
    if (!roundName.trim()) {
      toast.error('Round name is required');
      return;
    }
    const target = Number(targetAmount);
    if (!target || target <= 0) {
      toast.error('Target amount must be greater than 0');
      return;
    }
    setSubmitting(true);
    const res = await syndicationService.createRound({
      round_name: roundName.trim(),
      startup_id: startupId === '' ? null : startupId,
      project_id: projectId === '' ? null : projectId,
      pipeline_id: pipelineId === '' ? null : pipelineId,
      target_amount: target,
      minimum_commitment:
        minimumCommitment === '' ? null : Number(minimumCommitment),
      maximum_commitment:
        maximumCommitment === '' ? null : Number(maximumCommitment),
      instrument_type: instrumentType || null,
      spv_carry_percent: carry === '' ? null : Number(carry),
      spv_management_fee_percent: mgmtFee === '' ? null : Number(mgmtFee),
      spv_admin_fee: adminFee === '' ? null : Number(adminFee),
      co_invest_visibility: visibility,
      status,
      open_date: openDate || null,
      close_date: closeDate || null,
    });
    setSubmitting(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data) {
      toast.success('Syndication round created');
      navigate(`/syndication/${res.data.id}`);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl">
        <Link
          to="/syndication"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to syndication
        </Link>

        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Create Syndication Round
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Configure the round's target, terms, and co-invest visibility. You
            can add commitments once created.
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
              Round name
            </label>
            <input
              type="text"
              value={roundName}
              onChange={(e) => setRoundName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="e.g. Acme Series Seed Syndicate"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Pipeline
              </label>
              <select
                value={pipelineId}
                onChange={(e) =>
                  setPipelineId(
                    e.target.value === '' ? '' : Number(e.target.value)
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">None</option>
                {pipelines.map((p) => {
                  const inv =
                    p.investor?.full_name || `Investor #${p.investor_id}`;
                  const target =
                    p.project?.project_name ||
                    p.startup?.name ||
                    `Pipeline #${p.id}`;
                  return (
                    <option key={p.id} value={p.id}>
                      #{p.id} · {inv} → {target}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Project
              </label>
              <select
                value={projectId}
                onChange={(e) =>
                  setProjectId(
                    e.target.value === '' ? '' : Number(e.target.value)
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">None</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.project_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Startup ID
              </label>
              <input
                type="number"
                value={startupId}
                onChange={(e) =>
                  setStartupId(
                    e.target.value === '' ? '' : Number(e.target.value)
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Target amount (USD)
              </label>
              <input
                type="number"
                value={targetAmount}
                onChange={(e) => setTargetAmount(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="e.g. 500000"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Min commitment
              </label>
              <input
                type="number"
                value={minimumCommitment}
                onChange={(e) => setMinimumCommitment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Optional"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Max commitment
              </label>
              <input
                type="number"
                value={maximumCommitment}
                onChange={(e) => setMaximumCommitment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Instrument type
              </label>
              <select
                value={instrumentType}
                onChange={(e) => setInstrumentType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="safe">SAFE</option>
                <option value="note">Convertible Note</option>
                <option value="equity">Priced Equity</option>
                <option value="warrant">Warrant</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Status
              </label>
              <select
                value={status}
                onChange={(e) =>
                  setStatus(e.target.value as SyndicationStatus)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="draft">Draft</option>
                <option value="open">Open</option>
                <option value="soft_closing">Soft closing</option>
                <option value="hard_closing">Hard closing</option>
                <option value="closed">Closed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              SPV terms
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                  Carry %
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={carry}
                  onChange={(e) => setCarry(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                  Mgmt fee %
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={mgmtFee}
                  onChange={(e) => setMgmtFee(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                  Admin fee ($)
                </label>
                <input
                  type="number"
                  value={adminFee}
                  onChange={(e) => setAdminFee(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Optional"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-2">
              Co-invest visibility
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {(
                [
                  {
                    value: 'transparent',
                    label: 'Transparent',
                    desc: 'All co-investors visible',
                  },
                  {
                    value: 'anonymized',
                    label: 'Anonymized',
                    desc: 'Names hidden; shown as "Investor #N"',
                  },
                  {
                    value: 'lead_only',
                    label: 'Lead only',
                    desc: 'Only lead investors named',
                  },
                ] as Array<{
                  value: CoInvestVisibility;
                  label: string;
                  desc: string;
                }>
              ).map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-2 border rounded-lg p-3 cursor-pointer ${
                    visibility === opt.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="visibility"
                    value={opt.value}
                    checked={visibility === opt.value}
                    onChange={() => setVisibility(opt.value)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {opt.label}
                    </div>
                    <div className="text-xs text-gray-500">{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-gray-200 pt-4">
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Open date
              </label>
              <input
                type="date"
                value={openDate}
                onChange={(e) => setOpenDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Close date
              </label>
              <input
                type="date"
                value={closeDate}
                onChange={(e) => setCloseDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Link
              to="/syndication"
              className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              Cancel
            </Link>
            <button
              onClick={submit}
              disabled={submitting}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Create
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NewSyndicationRoundPage;
